/**
 * HumanoidLoader — Loads and caches humanoid GLB *meshes* for players.
 *
 * # Architecture
 *
 * This is the Layer-3 (renderer) consumer of the Layer-2 manifest
 * (AssetManifest.js). The loader knows nothing about football roles
 * or assignment verbs — it only knows how to take a `mesh_id` from
 * the manifest, fetch the GLB from the football domain pack's asset
 * route, parse it, and hand the renderer a fresh skinned-mesh clone
 * with an attached AnimationMixer.
 *
 * Animation clips are fetched separately by ClipLoader (lazy, per
 * verb), then attached to the mesh's mixer at play time. The mesh
 * GLB itself only needs to ship the bind pose + skeleton — embedded
 * clips are loaded but optional.
 *
 * # Mesh contract
 *
 *   * Single SkinnedMesh root (or merged via gltf-transform)
 *   * Mixamo bone naming (mixamorig*) so the helmet/pads/ball
 *     attachment code + ClipLoader animations find the right bones
 *   * Y-up, origin at feet, scale 1u = 1m
 *   * Optional `userData.uniformMaskMaterial = <materialName>` on the
 *     SkinnedMesh — when present, only that material slot is tinted
 *     to team color; when absent, the whole mesh is tinted.
 *
 * # Caching
 *
 * One in-flight promise per mesh_id + permanent cache after first
 * resolve. Per-player clones via SkeletonUtils.clone() so every player
 * gets an independent skeleton + AnimationMixer.
 *
 * # Backward compatibility
 *
 * `archetypeForRole(role)` and `instantiateHumanoid(archetype)` keep
 * their old signatures so callers that pre-date the manifest layer
 * (PlayerModel.createPlayer, FootballScene3D.preloadHumanoid) keep
 * working unchanged. Internally they now delegate to the manifest.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import {
  MESHES, resolveMesh, resolveMeshForRole, resolveRole,
  DEFAULT_MESH_ID, FALLBACK_MESH_ID, assetUrl,
} from './AssetManifest';

// Legacy archetype routing — kept for backward compatibility with code
// that calls archetypeForRole() directly. New code should use the role
// id directly with the manifest.
export const ARCHETYPES = ['lineman', 'skill', 'qb', 'base'];

export function archetypeForRole(role) {
  // The manifest's role entry now carries an `archetype_hint` string
  // for any caller that still wants the old archetype taxonomy.
  if (!role) return 'base';
  const r = resolveRole(role);
  return r.archetype_hint || 'base';
}

/**
 * Resolve a legacy archetype (or a manifest mesh id) to a mesh_id we
 * can pass to the manifest.
 *
 *   * Manifest mesh ids ("xbot", "vanguard", "synty_lineman", "synty_skill",
 *     "synty_qb", or any future addition) pass through unchanged so the
 *     manifest's ROLES table can route per-role to per-archetype meshes.
 *   * Legacy archetype tokens ("lineman" / "skill" / "qb" / "base"),
 *     which pre-date the manifest, fall through to DEFAULT_MESH_ID
 *     since they're informational only now (see archetype_hint on
 *     each ROLES entry for the modern equivalent).
 *
 * The `MESHES[id]` membership test is what unlocks the Synty pack:
 * a synty_* id added to the manifest immediately becomes a valid
 * mesh id this loader will fetch (and fall back from on 404).
 */
function _meshIdFor(archetypeOrMeshId) {
  if (!archetypeOrMeshId) return DEFAULT_MESH_ID;
  if (MESHES[archetypeOrMeshId]) return archetypeOrMeshId;
  return DEFAULT_MESH_ID;
}

const _loadPromises = {};   // mesh_id -> Promise<{template, animations}>
const _cached = {};         // mesh_id -> {template, animations}
const _resolvedMeshId = {}; // requested mesh_id -> actually served mesh_id (after fallback)

function _loadFile(file) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    loader.load(
      assetUrl(file),
      (gltf) => resolve(gltf),
      undefined,
      (err) => reject(err),
    );
  });
}

function _extractCached(gltf) {
  const root = gltf.scene;
  let skinnedMesh = null;
  root.traverse((obj) => {
    if (obj.isSkinnedMesh && !skinnedMesh) skinnedMesh = obj;
  });
  if (!skinnedMesh) {
    throw new Error('HumanoidLoader: no SkinnedMesh in GLB');
  }
  skinnedMesh.castShadow = false;
  skinnedMesh.receiveShadow = false;
  return { template: root, animations: gltf.animations || [] };
}

function _load(meshId = DEFAULT_MESH_ID) {
  if (_cached[meshId]) return Promise.resolve(_cached[meshId]);
  if (_loadPromises[meshId]) return _loadPromises[meshId];

  _loadPromises[meshId] = (async () => {
    const def = resolveMesh(meshId);
    try {
      const gltf = await _loadFile(def.glb);
      _cached[meshId] = _extractCached(gltf);
      _resolvedMeshId[meshId] = meshId;
      return _cached[meshId];
    } catch (err) {
      // Manifest's primary mesh missing — fall back to the legacy
      // Vanguard so an asset upload typo doesn't take the whole 3D
      // viewer offline. Coaches see a degraded mesh but the play
      // still renders + animations still play.
      if (meshId === FALLBACK_MESH_ID) {
        delete _loadPromises[meshId];
        throw err;
      }
      console.warn(
        `HumanoidLoader: ${def.glb} unavailable, falling back to ${FALLBACK_MESH_ID} for mesh_id "${meshId}"`,
        err && err.message,
      );
      const fallback = await _load(FALLBACK_MESH_ID);
      _cached[meshId] = fallback;
      _resolvedMeshId[meshId] = FALLBACK_MESH_ID;
      return fallback;
    }
  })();
  return _loadPromises[meshId];
}

/**
 * Pre-load a mesh by manifest id ("xbot", "vanguard") OR by legacy
 * archetype ("lineman" | "skill" | "qb" | "base"). Both signatures
 * are supported so callers can migrate at their own pace.
 */
export function preloadHumanoid(idOrArchetype = DEFAULT_MESH_ID) {
  return _load(_meshIdFor(idOrArchetype));
}

export function isHumanoidReady(idOrArchetype = DEFAULT_MESH_ID) {
  return _cached[_meshIdFor(idOrArchetype)] !== undefined;
}

/**
 * For tests + diagnostics: which mesh actually served a request after
 * any fallback was applied.
 */
export function resolvedArchetype(idOrArchetype = DEFAULT_MESH_ID) {
  return _resolvedMeshId[_meshIdFor(idOrArchetype)] || null;
}

/** Test-only reset. Production never calls this. */
export function _resetForTests() {
  Object.keys(_loadPromises).forEach((k) => delete _loadPromises[k]);
  Object.keys(_cached).forEach((k) => delete _cached[k]);
  Object.keys(_resolvedMeshId).forEach((k) => delete _resolvedMeshId[k]);
}

/**
 * Create a fresh humanoid instance with its own skeleton and AnimationMixer.
 * Returns { root, mixer, clips, headBone, chestBone, hipsBone, archetype }.
 *
 * Accepts either a manifest mesh_id ("xbot", "vanguard") or a legacy
 * archetype name ("qb", "lineman", "skill", "base") — they all resolve
 * through the manifest. Use the manifest mesh_id directly in new code.
 *
 * `clips` is the dict of any AnimationClips embedded in the mesh GLB
 * (typically just the bind-pose Idle). Per-verb clips are loaded
 * separately via ClipLoader.loadClip() and registered onto the mixer
 * by PlayerModel at the time the verb is needed.
 */
export async function instantiateHumanoid(idOrArchetype = DEFAULT_MESH_ID) {
  const meshId = _meshIdFor(idOrArchetype);
  const { template, animations } = await _load(meshId);

  const root = cloneSkeleton(template);

  const mixer = new THREE.AnimationMixer(root);
  const clips = {};
  animations.forEach((clip) => {
    clips[clip.name] = clip;
  });

  let headBone = null;
  let chestBone = null;
  let hipsBone = null;
  let leftShoulder = null;
  let rightShoulder = null;
  root.traverse((obj) => {
    if (!obj.isBone) return;
    const name = obj.name || '';
    if (!headBone && /Head$/i.test(name)) headBone = obj;
    if (!chestBone && /(Spine2|Spine1|Chest)$/i.test(name)) chestBone = obj;
    if (!hipsBone && /Hips$/i.test(name)) hipsBone = obj;
    if (!leftShoulder && /LeftShoulder$/i.test(name)) leftShoulder = obj;
    if (!rightShoulder && /RightShoulder$/i.test(name)) rightShoulder = obj;
  });

  if (!chestBone) {
    root.traverse((obj) => {
      if (!chestBone && obj.isBone && /Spine/i.test(obj.name || '')) chestBone = obj;
    });
  }

  return {
    root,
    mixer,
    clips,
    headBone,
    chestBone,
    hipsBone,
    leftShoulder,
    rightShoulder,
    // `archetype` retained for backward compatibility — it's now the
    // resolved manifest mesh_id ("xbot" or "vanguard").
    archetype: meshId,
    meshId,
    resolvedArchetype: _resolvedMeshId[meshId] || meshId,
  };
}
